import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import { ChevronDown, ChevronUp, MapPin, Save } from 'lucide-react';

import { Button } from 'components/Button';
import { Dialog, DialogContent } from 'components/Dialog';
import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Input } from 'components/input/Input';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { useToast } from 'hooks/useToaster';
import { createLocation } from 'lib/api/createLocation';
import { LoadStopTypes, TMSLocation } from 'types/Load';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';

interface LocationCreatorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLocationCreated: (location: TMSLocation) => void;
  tmsID: number;
  stopType?: LoadStopTypes;
  initialData?: Partial<LocationFormData>;
  tradingPartnerNum?: string; // Optional: trading partner number from selected customer
}

interface LocationFormData {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  phone: string;
  email: string;
  specialInstructions: string;
}

export default function LocationCreatorDialog({
  isOpen,
  onClose,
  onLocationCreated,
  tmsID,
  stopType = undefined,
  initialData,
  tradingPartnerNum,
}: LocationCreatorDialogProps) {
  const [showAdditionalOptions, setShowAdditionalOptions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const formMethods = useForm<LocationFormData>({
    defaultValues: {
      name: initialData?.name || '',
      addressLine1: initialData?.addressLine1 || '',
      addressLine2: initialData?.addressLine2 || '',
      city: initialData?.city || '',
      state: initialData?.state || '',
      zipcode: initialData?.zipcode || '',
      country: initialData?.country || 'United States',
      phone: initialData?.phone || '',
      email: initialData?.email || '',
      specialInstructions: initialData?.specialInstructions || '',
    },
  });

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = formMethods;

  const handleClose = () => {
    reset();
    setShowAdditionalOptions(false);
    onClose();
  };

  useEffect(() => {
    if (isOpen) {
      reset(
        {
          name: initialData?.name || '',
          addressLine1: initialData?.addressLine1 || '',
          addressLine2: initialData?.addressLine2 || '',
          city: initialData?.city || '',
          state: initialData?.state || '',
          zipcode: initialData?.zipcode || '',
          country: initialData?.country || 'United States',
          phone: initialData?.phone || '',
          email: initialData?.email || '',
          specialInstructions: initialData?.specialInstructions || '',
        },
        {
          keepDefaultValues: false,
        }
      );

      if (
        initialData?.phone ||
        initialData?.email ||
        initialData?.specialInstructions
      ) {
        setShowAdditionalOptions(true);
      }
    }
  }, [isOpen, initialData, reset]);

  const onSubmit = async (data: LocationFormData) => {
    setIsLoading(true);

    try {
      const locationPayload: TMSLocation = {
        ID: undefined,
        externalTMSID: '', // Will be assigned by backend
        name: data.name,
        addressLine1: data.addressLine1,
        addressLine2: data.addressLine2 || '',
        city: data.city,
        state: data.state,
        zipCode: data.zipcode,
        country: data.country,
        contact: '',
        phone: data.phone || '',
        email: data.email || '',
        apptRequired: false,
        isShipper: !stopType || stopType === LoadStopTypes.Pickup,
        isConsignee: !stopType || stopType === LoadStopTypes.Consignee,
      };

      const options: {
        specialInstructions?: string;
        tradingPartnerNum?: string;
      } = {};

      if (data.specialInstructions) {
        options.specialInstructions = data.specialInstructions;
      }

      if (tradingPartnerNum) {
        options.tradingPartnerNum = tradingPartnerNum;
      }

      const res = await createLocation({
        location: locationPayload,
        tmsID,
        options: Object.keys(options).length > 0 ? options : undefined,
      });

      if (res.isOk()) {
        toast({
          description: 'Location created successfully!',
          variant: 'success',
        });
        onLocationCreated(res.value.location);
        handleClose();
        return;
      } else {
        toast({
          description: res.error.message || 'Error creating location.',
          variant: 'destructive',
        });
      }
    } catch {
      toast({
        description: 'Error creating location.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='max-w-md p-0 max-h-[90vh] flex flex-col border border-neutral-300'>
        <ExtendedFormProvider aiDefaultValues={!!initialData} aiIconOnly={true}>
          <FormProvider {...formMethods}>
            <Flex
              justify='between'
              align='center'
              className='bg-brand-main px-4 py-3 text-neutral-50 border-b rounded-t-lg [&>div>svg]:stroke-white!'
            >
              <Flex align='center' gap='sm'>
                <MapPin className='h-5 w-5' />
                <Typography variant='h3' className='text-neutral-50'>
                  Create Location
                </Typography>
              </Flex>
            </Flex>

            <form
              onSubmit={(e) => {
                e.stopPropagation();
                handleSubmit(onSubmit)(e);
              }}
              className='flex flex-1 flex-col overflow-hidden'
            >
              <div className='flex-1 space-y-4 overflow-y-auto px-4 pb-4'>
                {/* Location Name & Address Section */}
                <div className='p-3 pt-0'>
                  <div>
                    <Label htmlFor='name' required name='name'>
                      <Typography variant='body' weight='semibold'>
                        Location Name
                      </Typography>
                    </Label>
                    <Input
                      id='name'
                      {...register('name', {
                        required: 'Location name is required',
                      })}
                      placeholder='Enter location name'
                      className={`mt-2 ${errors.name ? 'border-error-500' : ''}`}
                    />
                    {errors.name && (
                      <Typography
                        variant='body-sm'
                        className='text-error-500 mt-1'
                      >
                        {errors.name.message}
                      </Typography>
                    )}
                  </div>

                  <div className='mt-2'>
                    <Typography variant='body' weight='semibold'>
                      Address
                    </Typography>
                  </div>

                  <div className='mt-2'>
                    <Label
                      htmlFor='addressLine1'
                      required
                      name='addressLine1'
                      className='mb-1 inline-block'
                    >
                      Street Address
                    </Label>
                    <Input
                      id='addressLine1'
                      {...register('addressLine1', {
                        required: 'Street address is required',
                      })}
                      placeholder='123 Main Street'
                      className={errors.addressLine1 ? 'border-error-500' : ''}
                    />
                    {errors.addressLine1 && (
                      <Typography variant='body-sm' className='text-error-500'>
                        {errors.addressLine1.message}
                      </Typography>
                    )}
                  </div>

                  <div className='mt-2'>
                    <Label
                      htmlFor='addressLine2'
                      name='addressLine2'
                      className='mb-1 inline-block'
                    >
                      Address Line 2
                    </Label>
                    <Input
                      id='addressLine2'
                      {...register('addressLine2')}
                      placeholder='Suite, unit, building, floor, etc.'
                    />
                  </div>

                  <Grid cols='2' gap='sm' className='mt-2'>
                    <div>
                      <Label
                        htmlFor='city'
                        required
                        name='city'
                        className='mb-1 inline-block'
                      >
                        City
                      </Label>
                      <Input
                        id='city'
                        {...register('city', { required: 'City is required' })}
                        placeholder='City'
                        className={errors.city ? 'border-error-500' : ''}
                      />
                      {errors.city && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.city.message}
                        </Typography>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor='state'
                        required
                        name='state'
                        className='mb-1 inline-block'
                      >
                        State
                      </Label>
                      <Input
                        id='state'
                        {...register('state', {
                          required: 'State is required',
                        })}
                        placeholder='State'
                        className={errors.state ? 'border-error-500' : ''}
                      />
                      {errors.state && (
                        <Typography
                          variant='body-sm'
                          className='text-error-500'
                        >
                          {errors.state.message}
                        </Typography>
                      )}
                    </div>
                  </Grid>

                  <div className='mt-2'>
                    <Label
                      htmlFor='zipcode'
                      required
                      name='zipcode'
                      className='mb-1 inline-block'
                    >
                      Zip Code
                    </Label>
                    <Input
                      id='zipcode'
                      {...register('zipcode', {
                        required: 'Zip code is required',
                      })}
                      placeholder='12345'
                      className={errors.zipcode ? 'border-error-500' : ''}
                    />
                    {errors.zipcode && (
                      <Typography variant='body-sm' className='text-error-500'>
                        {errors.zipcode.message}
                      </Typography>
                    )}
                  </div>

                  <div className='mt-2'>
                    <Label
                      htmlFor='country'
                      name='country'
                      className='mb-1 inline-block'
                    >
                      Country
                    </Label>
                    <Controller
                      name='country'
                      control={control}
                      render={({ field }) => (
                        <Select
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value='United States'>
                              United States
                            </SelectItem>
                            <SelectItem value='Canada'>Canada</SelectItem>
                            <SelectItem value='Mexico'>Mexico</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                </div>

                {/* Additional Options Toggle */}
                <Button
                  buttonNamePosthog={null}
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={() =>
                    setShowAdditionalOptions(!showAdditionalOptions)
                  }
                  className='w-full justify-between'
                >
                  <Typography variant='body-sm'>More Options</Typography>
                  {showAdditionalOptions ? (
                    <ChevronUp className='h-4 w-4' />
                  ) : (
                    <ChevronDown className='h-4 w-4' />
                  )}
                </Button>

                {showAdditionalOptions && (
                  <div className='p-3'>
                    <div>
                      <Label
                        htmlFor='phone'
                        name='phone'
                        className='mb-1 inline-block'
                      >
                        Phone
                      </Label>
                      <Input
                        id='phone'
                        {...register('phone')}
                        placeholder='(555) 123-4567'
                        type='tel'
                      />
                    </div>

                    <div className='mt-2'>
                      <Label
                        htmlFor='email'
                        name='email'
                        className='mb-1 inline-block'
                      >
                        Email
                      </Label>
                      <Input
                        id='email'
                        {...register('email')}
                        placeholder='contact@example.com'
                        type='email'
                      />
                    </div>

                    <div className='mt-2'>
                      <Label
                        htmlFor='specialInstructions'
                        name='specialInstructions'
                        className='mb-1 inline-block'
                      >
                        Special Instructions
                      </Label>
                      <Input
                        id='specialInstructions'
                        {...register('specialInstructions')}
                        placeholder='Special delivery instructions'
                      />
                    </div>
                  </div>
                )}

                <div className='rounded border-l-4 border-info-500 bg-info-50 p-3'>
                  <Typography variant='body-sm' className='text-neutral-700'>
                    This location will be saved to your TMS.
                  </Typography>
                </div>
              </div>

              {/* Footer Actions */}
              <Flex
                gap='sm'
                className='border-t border-neutral-300 bg-neutral-100 px-4 py-3 rounded-b-lg'
              >
                <Button
                  buttonNamePosthog={ButtonNamePosthog.CancelLocationCreation}
                  type='button'
                  variant='outline'
                  onClick={handleClose}
                  className='flex-1 text-sm'
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  buttonNamePosthog={ButtonNamePosthog.SaveLocation}
                  type='submit'
                  className='flex-1 gap-2 [&>svg]:stroke-white! text-sm'
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>Saving...</>
                  ) : (
                    <>
                      <Save className='h-4 w-4' />
                      Save Location
                    </>
                  )}
                </Button>
              </Flex>
            </form>
          </FormProvider>
        </ExtendedFormProvider>
      </DialogContent>
    </Dialog>
  );
}
